import { useCallback, useEffect, useMemo, useState } from 'react';

import { Field, Modal, Notice } from '@/components/Modal';
import { EmptyCard, LoadingBlock } from '@/components/PageHeader';
import { IncentiveAwardTable, type AwardPatch } from '@/pages/hr/IncentiveAwardTable';
import {
  OWNER_TYPE_LABELS,
  previewAmount,
  statusLabel,
  useMyIncentive,
  useMyIncentiveDetail,
  useSaveMyIncentive,
  useSubmitMyIncentive,
  useUnlockMyIncentive,
  won,
  type AwardWrite,
  type IncentiveAward,
  type IncentiveStatus,
  type MyIncentive,
} from '@/pages/hr/useIncentive';

/**
 * 마이페이지 › 복지관리 › **성과급** (V308 · 사용자 지시 2026-09-21).
 *
 * ## 비밀번호를 한 번 더 받는다
 * 사용자 지시: 「항목이 있을 경우에 인사고과나 급여명세서처럼 비밀번호 한번 더 입력해야 나오게」.
 *
 * **서버가 금액을 안 보낸다.** 잠긴 요청은 건수만 받고, 금액은 재인증을 지나야 온다 — 화면에서
 * 감추기만 하면 응답에 이미 값이 있어 개발자 도구로 보인다(급여·고과와 같은 구조).
 *
 * 열어 둔 값은 **이 컴포넌트가 들고 있다**. 캐시에 넣으면 화면을 떠난 뒤에도 금액이 메모리에
 * 남는다 — 탭을 벗어나면 언마운트와 함께 사라져 저절로 다시 잠긴다.
 *
 * ## 항목이 없으면 묻지 않는다
 * 둘 다 0 이면 「없습니다」 라고 적는다. 없는 자료에 비밀번호를 묻는 화면은 사람을 두 번
 * 헛걸음시킨다(급여·고과의 `available` 과 같은 판단).
 *
 * ## 한 탭이 두 사람에게 다른 것을 보여 준다
 * 대부분의 직원에게 이 탭은 「내가 얼마 받았나」 다 — **확정된 줄만** 보인다. 현장대리인·팀장에게는
 * 거기에 **평가할 자리**가 함께 선다. 화면을 둘로 나누지 않은 이유는, 평가하는 사람도 자기 몫을
 * 받는 사람이라 두 화면을 오가게 되기 때문이다.
 *
 * ## 확정 전 금액은 보이지 않는다
 * 협의 중인 숫자는 아직 그 사람의 돈이 아니다. 본 금액과 확정액이 다르면 그 차이를 설명할 자리가
 * 마이페이지에는 없다 — 설명이 필요한 숫자는 보여 주지 않는다(서버도 안 내려준다).
 */

const HEAD: React.CSSProperties = { padding: '8px 12px', textAlign: 'left', whiteSpace: 'nowrap' };
const CELL: React.CSSProperties = { padding: '8px 12px', whiteSpace: 'nowrap' };

export function IncentivePanel() {
  const gate = useMyIncentive();
  /* 열어 둔 금액. 캐시가 아니라 여기 있어서, 탭을 벗어나면 함께 사라진다. */
  const [opened, setOpened] = useState<MyIncentive | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);

  if (gate.isPending) return <LoadingBlock />;

  const counts = gate.data ?? { leadRoundCount: 0, awardCount: 0 };
  if (counts.leadRoundCount === 0 && counts.awardCount === 0) {
    return <EmptyCard>성과급 내역이 없습니다.</EmptyCard>;
  }
  if (!opened) {
    return <PasswordGate onUnlock={setOpened} />;
  }

  return (
    <div className="space-y-4">
      {opened.leadRounds.length > 0 && (
        <div className="card p-4 space-y-3">
          <h3 style={{ fontSize: '14px', fontWeight: 700 }}>평가</h3>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)', color: 'var(--text-secondary)' }}>
                  <th style={HEAD}>구분</th>
                  <th style={HEAD}>소속</th>
                  <th style={HEAD}>기간</th>
                  <th style={HEAD}>평가등급</th>
                  <th style={{ ...HEAD, textAlign: 'right' }}>기준 금액</th>
                  <th style={{ ...HEAD, textAlign: 'right' }}>지급금 한도</th>
                  <th style={{ ...HEAD, textAlign: 'right' }}>지급액 합계</th>
                  <th style={{ ...HEAD, textAlign: 'right' }}>잔여</th>
                  <th style={HEAD}>상태</th>
                </tr>
              </thead>
              <tbody>
                {opened.leadRounds.map((row) => (
                  <tr
                    key={row.id}
                    style={{ borderTop: '1px solid var(--border)', cursor: 'pointer' }}
                    onClick={() => setOpenId(row.id)}
                  >
                    <td style={{ ...CELL, color: 'var(--text-secondary)' }}>{OWNER_TYPE_LABELS[row.ownerType]}</td>
                    <td style={{ ...CELL, fontWeight: 600 }}>{row.ownerName}</td>
                    <td style={{ ...CELL, color: 'var(--text-secondary)' }}>
                      {row.constructionStartDate ?? ''} ~ {row.constructionEndDate ?? ''}
                    </td>
                    <td style={CELL}>{row.grade ?? ''}</td>
                    <td style={{ ...CELL, textAlign: 'right' }}>{won(row.baseAmount)}</td>
                    <td style={{ ...CELL, textAlign: 'right' }}>{won(row.budgetAmount)}</td>
                    <td style={{ ...CELL, textAlign: 'right', fontWeight: 600 }}>{won(row.totalAmount)}</td>
                    <td
                      style={{
                        ...CELL,
                        textAlign: 'right',
                        color: (row.remainingAmount ?? 0) < 0 ? 'var(--danger)' : 'var(--text-secondary)',
                      }}
                    >
                      {won(row.remainingAmount)}
                    </td>
                    <td style={CELL}>{statusLabel(row)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="card p-4 space-y-3">
        <h3 style={{ fontSize: '14px', fontWeight: 700 }}>내 성과급</h3>
        {opened.myAwards.length === 0 ? (
          <EmptyCard>확정된 성과급이 없습니다.</EmptyCard>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)', color: 'var(--text-secondary)' }}>
                  <th style={HEAD}>소속</th>
                  <th style={HEAD}>기간</th>
                  <th style={HEAD}>평가등급</th>
                  <th style={{ ...HEAD, textAlign: 'right' }}>참여 개월</th>
                  <th style={{ ...HEAD, textAlign: 'right' }}>기여도(%)</th>
                  <th style={{ ...HEAD, textAlign: 'right' }}>지급액</th>
                  <th style={HEAD}>확정일</th>
                </tr>
              </thead>
              <tbody>
                {opened.myAwards.map((row) => (
                  <tr key={row.incentiveId} style={{ borderTop: '1px solid var(--border)' }}>
                    <td style={{ ...CELL, fontWeight: 600 }}>{row.ownerName}</td>
                    <td style={{ ...CELL, color: 'var(--text-secondary)' }}>
                      {row.constructionStartDate ?? ''} ~ {row.constructionEndDate ?? ''}
                    </td>
                    <td style={CELL}>{row.grade ?? ''}</td>
                    <td style={{ ...CELL, textAlign: 'right' }}>{row.months ?? ''}</td>
                    <td style={{ ...CELL, textAlign: 'right' }}>{row.contributionRate ?? ''}</td>
                    <td style={{ ...CELL, textAlign: 'right', fontWeight: 700 }}>{won(row.payableAmount)}</td>
                    <td style={{ ...CELL, color: 'var(--text-secondary)' }}>
                      {row.confirmedAt ? row.confirmedAt.slice(0, 10) : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {openId && (
        <EvaluationModal
          id={openId}
          onClose={() => setOpenId(null)}
          onChanged={(status, total) => setOpened((prev) => (prev === null ? prev : {
            ...prev,
            /*
              목록은 열어 둔 값 안에 있다(캐시가 아니다). 저장·제출하고 창을 닫았을 때 그 줄이
              옛 상태로 남아 있으면, 방금 제출한 사람이 「안 된 건가」 하고 다시 누른다.
            */
            leadRounds: prev.leadRounds.map((row) => (row.id === openId
              ? {
                ...row,
                status,
                totalAmount: total,
                remainingAmount: row.budgetAmount === null ? null : row.budgetAmount - total,
              }
              : row)),
          }))}
        />
      )}
    </div>
  );
}

/**
 * 본인 확인. 급여·고과의 잠금 화면과 <b>같은 말, 같은 동작</b>이다 — 한 화면에서 배운 것이
 * 다른 화면에서 그대로 통해야 한다.
 */
function PasswordGate({ onUnlock }: { onUnlock: (data: MyIncentive) => void }) {
  const unlock = useUnlockMyIncentive();
  const [password, setPassword] = useState('');

  const submit = () => {
    if (!password.trim() || unlock.isPending) return;
    unlock.mutate(password, {
      onSuccess: (data) => {
        setPassword('');
        onUnlock(data);
      },
    });
  };

  return (
    <div className="mx-auto py-6" style={{ maxWidth: '320px' }}>
      <p className="text-center" style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
        성과급은 민감정보입니다.
      </p>
      <p className="text-center" style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>
        본인 확인을 위해 비밀번호를 입력해 주세요.
      </p>
      <div className="flex gap-2" style={{ marginTop: '12px' }}>
        <input
          className="input-field"
          type="password"
          autoComplete="current-password"
          aria-label="비밀번호"
          placeholder="비밀번호"
          value={password}
          onChange={(event) => {
            setPassword(event.target.value);
            unlock.reset();
          }}
          onKeyDown={(event) => event.key === 'Enter' && submit()}
          autoFocus
        />
        <button className="btn-primary flex-shrink-0" onClick={submit} disabled={!password.trim() || unlock.isPending}>
          {unlock.isPending ? '확인 중...' : '조회'}
        </button>
      </div>
      {unlock.isError && (
        <p className="text-center" style={{ fontSize: '12px', color: 'var(--danger)', marginTop: '8px' }}>
          {(unlock.error as Error).message}
        </p>
      )}
    </div>
  );
}

/**
 * 그 조직의 장이 기여도를 매기는 판. 제출하고 나면 같은 창이 조회로 바뀐다.
 *
 * <p>여기에는 비밀번호를 다시 묻지 않는다 — 잠금은 <b>탭을 열자마자 금액이 펼쳐지는 것</b>을
 * 막는 장치이고, 이 창은 평가를 하려고 스스로 눌러 여는 자리다.
 */
function EvaluationModal({
  id,
  onClose,
  onChanged,
}: {
  id: string;
  onClose: () => void;
  /** 저장·제출이 돌려준 새 상태와 합계. 목록 줄을 그 자리에서 맞춘다. */
  onChanged: (status: IncentiveStatus, total: number) => void;
}) {
  const detail = useMyIncentiveDetail(id);
  const save = useSaveMyIncentive();
  const submit = useSubmitMyIncentive();

  const [rows, setRows] = useState<IncentiveAward[]>([]);
  const [note, setNote] = useState('');

  useEffect(() => {
    if (!detail.data) return;
    setRows(detail.data.awards);
    setNote(detail.data.siteNote ?? '');
  }, [detail.data]);

  const patch = useCallback((personnelId: string, next: AwardPatch) => {
    setRows((prev) => prev.map((row) => (row.personnelId === personnelId ? { ...row, ...next } : row)));
  }, []);

  const base = detail.data?.baseAmount ?? null;
  const total = useMemo(
    () => rows.reduce((sum, row) => sum + (row.adjustedAmount ?? previewAmount(base, row.contributionRate) ?? 0), 0),
    [rows, base],
  );

  if (detail.isPending || !detail.data) {
    return <Modal title="성과급" onClose={onClose}><LoadingBlock /></Modal>;
  }

  const data = detail.data;
  const remaining = data.budgetAmount === null ? null : data.budgetAmount - total;
  const error = (save.error ?? submit.error) as Error | null;
  const writes = () => rows.map(toWrite);

  return (
    <Modal
      title={`${data.ownerName} 성과급 — ${statusLabel(data)}`}
      maxWidth="1180px"
      onClose={onClose}
      footer={
        data.siteEditable ? (
          <>
            <button className="btn-secondary" onClick={onClose}>닫기</button>
            <span style={{ flex: 1 }} />
            <button
              className="btn-secondary"
              disabled={save.isPending}
              onClick={() => save.mutate({ id, awards: writes(), note },
                { onSuccess: (next) => onChanged(next.status, next.totalAmount) })}
            >
              임시저장
            </button>
            <button
              className="btn-primary"
              disabled={submit.isPending}
              onClick={() => submit.mutate({ id, awards: writes(), note }, {
                onSuccess: (next) => {
                  onChanged(next.status, next.totalAmount);
                  onClose();
                },
              })}
            >
              본사 제출
            </button>
          </>
        ) : (
          <button className="btn-secondary" onClick={onClose}>닫기</button>
        )
      }
    >
      <div className="space-y-4">
        {error && <Notice tone="error">{error.message}</Notice>}

        <div className="flex items-center gap-4 flex-wrap" style={{ fontSize: '13px' }}>
          <span>기준 금액 <b>{won(data.baseAmount)}</b></span>
          <span>지급금 한도 <b>{won(data.budgetAmount)}</b></span>
          <span>합계 <b>{won(total)}</b></span>
          <span style={{ color: (remaining ?? 0) < 0 ? 'var(--danger)' : 'var(--text-secondary)' }}>
            잔여 {won(remaining)}
          </span>
        </div>

        <IncentiveAwardTable
          rows={rows}
          baseAmount={data.baseAmount}
          ownerType={data.ownerType}
          mode={data.siteEditable ? 'SITE' : 'READ'}
          onChange={patch}
        />

        <Field label="총평">
          <textarea
            className="input-field"
            rows={2}
            disabled={!data.siteEditable}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </Field>

        {/* 반려 사유도 조정 의견도 이 칸으로 온다 — 평가자에게는 둘 다 「본사가 한 말」 이다. */}
        {data.hqNote && (
          <Field label="본사 의견">
            <div className="input-field" style={{ background: 'var(--bg-hover)', whiteSpace: 'pre-wrap' }}>
              {data.hqNote}
            </div>
          </Field>
        )}
      </div>
    </Modal>
  );
}

/** 보내는 한 줄. 조정액은 보내지 않는다 — 본사의 칸이고, 서버가 직전 값을 지킨다. */
function toWrite(row: IncentiveAward): AwardWrite {
  return {
    personnelId: row.personnelId,
    joinedOn: row.joinedOn,
    leftOn: row.leftOn,
    months: row.months,
    contributionRate: row.contributionRate,
    note: row.note,
  };
}
